/**
 * The header. Answers "what am I looking at" before anything has to load.
 *
 * The two identifiers are deliberately BOTH here and deliberately styled
 * differently. The join code is a short-lived, human, sayable handle for a live
 * room; the session id is the durable key that this page is addressed by and
 * that an operator will paste into a support conversation. Presenting them as
 * the same kind of thing is how somebody ends up mailing a join code to
 * identify a recording from last month.
 */

import * as React from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, Check, ChevronRight, Copy, LogIn, Server } from 'lucide-react'
import type { SessionDetailView } from '@era/shared'
import { Badge, LiveDot } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { formatDateTime, formatDuration, formatRelative } from '@/lib/format'

/**
 * PHONE NOTE. This block is the first thing on the screen and everything below
 * it — the player, the tabs, the transcript — is pushed down by exactly its
 * height, so on a 375x667 viewport its size decides how much of the recording
 * you can see when the page opens. At its desktop size it was ~260px, or 39% of
 * that screen, to say things most readers read once.
 *
 * It is compacted rather than pinned, on purpose: this is orientation, not a
 * control. It should be there when you arrive and gone when you are reading,
 * which is what scrolling away means. The player and the tab strip are the two
 * things that stay.
 */
export function SessionHeader({ view }: { view: SessionDetailView }) {
  return (
    <div className="space-y-2.5 sm:space-y-4">
      <Link
        to="/history"
        // `-my-2 py-2` is a 44px-tall target that occupies 20px of layout: the
        // padding grows the hit box, the negative margin gives the space back.
        //
        // Touch only, and that scoping is not caution — it is a bug that was
        // caught in a screenshot. Adjoining vertical margins COLLAPSE, so a
        // -8px bottom margin next to the container's +16px `space-y-4` resolves
        // to 8px, not to 16px: the desktop gap under the back link silently
        // halved. Below `sm` the container is `space-y-2.5` and the same
        // collapse is the tightening that is wanted there.
        className="tap44 inline-flex items-center gap-1.5 text-[0.8125rem] font-medium text-ink-muted transition-colors hover:text-ink max-sm:-my-2 max-sm:py-2"
      >
        <ArrowLeft className="size-3.5" />
        History
      </Link>

      <div className="flex flex-wrap items-center gap-2">
        <StateBadge state={view.state} />
        {/* WHAT WAS ACTUALLY KEPT, not what was kept when this app could only
            keep audio.
            
            This badge said "audio retained" on every recorded session,
            including one that also kept a 720p MP4 — which the same page then
            renders and plays about 400px lower. A reviewer reading the badge
            and a reviewer scrolling reached opposite conclusions about the same
            call, and the badge is the one that gets quoted.
            
            `video_recording_ever_enabled` is the honest signal: it is a fact
            about the session's history, not about whether an artifact happens
            to be ready right now (`video_ready`), so it stays true while the
            video is still being finalised. */}
        {view.recording_ever_enabled === false ? (
          <Badge tone="neutral">not recorded</Badge>
        ) : view.recording_ever_enabled ? (
          <Badge tone="neutral">
            {view.video_recording_ever_enabled ? 'audio + video retained' : 'audio retained'}
          </Badge>
        ) : (
          <Badge tone="neutral">recording status unknown</Badge>
        )}
        {view.join_code ? (
          <Badge tone="neutral">
            <span className="code-display text-[0.6875rem]">{view.join_code}</span>
          </Badge>
        ) : (
          // A session this app did not create. Said plainly rather than left as
          // a mysteriously empty join code. See FINDINGS.md #10.
          <Badge tone="neutral">not created here</Badge>
        )}

        <Rejoin view={view} />
      </div>

      {/* `text-2xl` on a phone, unchanged from `sm` up. At `text-3xl` a title
          of any real length was two 38px lines of Fraunces before the reader
          reached a single fact about the call. The voice is the same face at
          the same weight; only the size moves. */}
      <h1 className="display max-w-3xl text-2xl leading-tight text-ink sm:text-4xl">
        {view.title ?? 'Untitled session'}
      </h1>

      {/* Each separator is INSIDE the item it precedes rather than a sibling of
          it. As loose siblings in a wrapping flex row, a dot could be the last
          thing on a line — at 390px this rendered as "… · 2 people ·" with the
          node orphaned onto the next line behind a dangling separator. Bound to
          its item, a dot always has something after it. */}
      {/* `gap-x-2.5` matches the separator's own right margin, so each dot sits
          equidistant between the two facts it divides instead of hanging off
          the end of the one before it. */}
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-sm text-ink-muted">
        <Meta first title={formatDateTime(view.started_at ?? view.created_at)}>
          {formatRelative(view.started_at ?? view.created_at)}
        </Meta>
        <Meta>
          <span className="tabular">{formatDuration(view.duration_ms)}</span>
        </Meta>
        <Meta>
          {view.participants.length} {view.participants.length === 1 ? 'person' : 'people'}
        </Meta>
        {view.node ? (
          <Meta title="The SFU that hosted this room. A room is pinned to its node for its whole life and is never migrated.">
            <Server className="size-3.5" />
            {view.node}
          </Meta>
        ) : null}
      </div>

      {/* The durable facts: the id an operator pastes into a support thread,
          the retention date, and why the call ended.
          Below `sm` they are folded, and the fold is the whole reason the
          transcript is visible on arrival — at 390px this row wraps to two
          lines and costs ~46px to show three things nobody reads twice. The
          `<details>` idiom is the one `FilesPanel` already uses for the same
          judgement about diagnostics, so it is a repeat rather than a new
          gesture. Rendered twice rather than made conditional in JS: which
          version applies is a layout question, and CSS is where this app
          answers layout questions. */}
      <div className="hidden flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-dim sm:flex">
        <Facts view={view} />
      </div>

      <details className="group text-xs text-ink-dim sm:hidden">
        <summary className="tap44 -my-2 inline-flex cursor-pointer list-none items-center gap-1 py-2 font-medium [&::-webkit-details-marker]:hidden">
          <ChevronRight className="size-3.5 transition-transform group-open:rotate-90" />
          details
        </summary>
        <div className="flex flex-col items-start gap-1 pt-2">
          <Facts view={view} />
        </div>
      </details>
    </div>
  )
}

/**
 * The door back into a call that has not closed yet.
 *
 * Gated on `SessionDetail.joinable` and on NOTHING ELSE. That field is the
 * whole point of FINDINGS.md #19: "true only while a new token may join the
 * LiveKit room. It is false from `ended` onward even while post-call
 * processing continues." A button drawn from `state === 'live'` instead would
 * be offered for the entire window between the last person leaving and the
 * pipeline finishing — minutes of a button that mints a token for a room that
 * is gone, which surfaces as a 409 on a screen the user was told to expect a
 * call on. `null` (a snapshot written before the field existed) is not `true`,
 * so it draws nothing.
 *
 * `join_code` is required for the second, unrelated reason that `/call/$code`
 * is addressed by code: a session this app did not create has no code to send
 * anyone to, however joinable it is.
 */
function Rejoin({ view }: { view: SessionDetailView }) {
  if (view.joinable !== true || !view.join_code) return null
  return (
    <Button variant="primary" size="sm" asChild className="tap44">
      <Link to="/call/$code" params={{ code: view.join_code }}>
        <LogIn className="size-3.5" />
        Rejoin
      </Link>
    </Button>
  )
}

function Facts({ view }: { view: SessionDetailView }) {
  return (
    <>
      <CopyableId value={view.session_id} />
      {view.retention_due_at && view.state !== 'purged' ? (
        // With the year, always. A retention date a year out that renders as
        // "Aug 8" reads as this week, which is the opposite of what it says.
        <span title={formatDateTime(view.retention_due_at)}>
          {view.video_recording_ever_enabled ? 'audio and video kept until' : 'audio kept until'}{' '}
          {formatDate(view.retention_due_at)}
        </span>
      ) : null}
      {view.end_reason ? <span>ended: {view.end_reason.replace(/_/g, ' ')}</span> : null}
    </>
  )
}

/** One fact in the meta row, carrying its own leading separator. */
function Meta({
  children,
  first,
  title,
}: {
  children: React.ReactNode
  first?: boolean
  title?: string
}) {
  return (
    <span className="inline-flex items-center gap-1.5" title={title}>
      {first ? null : (
        <span aria-hidden className="mr-2.5 text-ink-dim">
          ·
        </span>
      )}
      {children}
    </span>
  )
}

function formatDate(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date)
}

function StateBadge({ state }: { state: SessionDetailView['state'] }) {
  switch (state) {
    case 'complete':
      return <Badge tone="ok">complete</Badge>
    case 'partial':
      return <Badge tone="warn">partial</Badge>
    case 'failed':
      return <Badge tone="critical">failed</Badge>
    case 'purged':
      return <Badge tone="neutral">purged</Badge>
    case 'live':
      return (
        <Badge tone="live">
          <LiveDot />
          live
        </Badge>
      )
    case 'ended':
      // NOT terminal, and the badge says so: the call is over but the pipeline
      // is still running. A reader who thinks `ended` means finished will read
      // the missing transcript as a bug.
      return <Badge tone="neutral">ended · processing</Badge>
    case 'pending':
      return <Badge tone="neutral">not started</Badge>
    default:
      return <Badge tone="neutral">unknown</Badge>
  }
}

function CopyableId({ value }: { value: string }) {
  const [copied, setCopied] = React.useState(false)

  React.useEffect(() => {
    if (!copied) return
    const timer = window.setTimeout(() => setCopied(false), 1600)
    return () => window.clearTimeout(timer)
  }, [copied])

  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => setCopied(true))
      }}
      // 17.3px tall as painted — the smallest target in the app, and it writes
      // to the clipboard, so a miss is a silent no-op the user cannot see.
      // `tap44` gives it a 44px touch box without changing the monospace line
      // it sits on. It overlaps the facts above and below it, which is safe
      // here precisely because those are plain spans: there is no neighbouring
      // control for the overlap to steal a tap from.
      className="tap44 ident inline-flex items-center gap-1.5 rounded transition-colors hover:text-ink"
      title="Copy the session id"
    >
      {value}
      {copied ? <Check className="size-3 text-ok" /> : <Copy className="size-3" />}
    </button>
  )
}
